import { Camera, Mic, Reply, X } from 'lucide-react';
import clsx from 'clsx';

import { formatClock } from '@/components/chat/VoicePlayer';
import type { ChatMessage, ChatReply } from '@/lib/chat';
import { mediaUrl } from '@/lib/media';
import { useAuthStore } from '@/store/auth.store';

/** Сообщение из ленты → цитата для полосы над полем ввода (сервер пришлёт свою). */
export function replyOf(m: ChatMessage): ChatReply {
  const a = m.attachment;
  return {
    id: m.id,
    authorId: m.author.id,
    authorName: m.author.name,
    text: m.text.replace(/\s+/g, ' ').trim(),
    attachment:
      a?.type === 'photo' ? { type: 'photo', url: a.url } : a?.type === 'voice' ? { type: 'voice', durationMs: a.durationMs } : null,
    deleted: !!m.deleted,
  };
}

function useAuthorLabel(reply: ChatReply): string {
  const me = useAuthStore((s) => s.manager?.id);
  return reply.authorId != null && reply.authorId === me ? 'Вы' : reply.authorName;
}

function Snippet({ reply }: { reply: ChatReply }) {
  if (reply.deleted) return <span className="italic">Сообщение удалено</span>;
  const a = reply.attachment;
  if (a?.type === 'voice') {
    return (
      <>
        <Mic size={13} className="mr-1 inline-block align-[-2px]" aria-hidden />
        Голосовое · {formatClock(a.durationMs)}
        {reply.text ? ` · ${reply.text}` : ''}
      </>
    );
  }
  if (a?.type === 'photo') {
    return (
      <>
        <Camera size={13} className="mr-1 inline-block align-[-2px]" aria-hidden />
        {reply.text || 'Фото'}
      </>
    );
  }
  return <>{reply.text}</>;
}

function thumbOf(reply: ChatReply): string | null {
  return reply.attachment?.type === 'photo' && !reply.deleted ? reply.attachment.url : null;
}

/** Цитата в пузыре ответа: тап — к исходному сообщению. */
export function ReplyQuote({ reply, own, onOpen }: { reply: ChatReply; own: boolean; onOpen: () => void }) {
  const who = useAuthorLabel(reply);
  const thumb = thumbOf(reply);
  return (
    <button
      type="button"
      onClick={onOpen}
      className={clsx(
        'flex w-full min-w-0 overflow-hidden rounded-lg border-l-[3px] text-left',
        own ? 'border-white/80 bg-white/15' : 'border-orange bg-orange/[0.07]',
      )}
    >
      <span className="min-w-0 flex-1 px-2 py-1">
        <span className={clsx('block truncate text-xs font-semibold', own ? 'text-white' : 'text-orange-700')}>{who}</span>
        <span className={clsx('line-clamp-2 break-words text-[13px] leading-snug', own ? 'text-white/85' : 'text-ink/60')}>
          <Snippet reply={reply} />
        </span>
      </span>
      {thumb ? (
        <img src={mediaUrl(thumb)} alt="" loading="lazy" decoding="async" className="h-12 w-12 shrink-0 object-cover" />
      ) : null}
    </button>
  );
}

/** Над полем ввода: на что отвечаем; крестик — передумал. */
export function ReplyBar({ reply, onCancel }: { reply: ChatReply; onCancel: () => void }) {
  const who = useAuthorLabel(reply);
  const thumb = thumbOf(reply);
  return (
    <div className="mb-2 flex items-center gap-2 rounded-xl bg-paper/80 py-1.5 pl-2.5 pr-1 ring-1 ring-line/60">
      <Reply size={18} className="shrink-0 text-orange" aria-hidden />
      <div className="min-w-0 flex-1 border-l-[3px] border-orange pl-2.5">
        <div className="truncate text-xs font-semibold text-orange-700">{who}</div>
        <div className="truncate text-sm text-ink/60">
          <Snippet reply={reply} />
        </div>
      </div>
      {thumb ? <img src={mediaUrl(thumb)} alt="" className="h-10 w-10 shrink-0 rounded-md object-cover" /> : null}
      <button
        type="button"
        onClick={onCancel}
        className="shrink-0 rounded-full p-2 text-ink/45 hover:bg-ink/5 hover:text-ink"
        aria-label="Не отвечать"
      >
        <X size={18} aria-hidden />
      </button>
    </div>
  );
}
